import { useEffect, useRef } from 'react';
import _, { isEqual } from 'lodash';

import { authorize } from 'services/auth';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { useAuthFailureCount } from 'store/auth-failure-count';
import Container from 'components/Container/Container';
import { PayrollProviderId } from 'constants/types';
import { getStatusCode, validateSubdomain } from './utils';
import LoadingSpinner from '../LoadingSpinner';
import { getProviderComponent } from './providerToComponentMap';
import RegisterProviderContainer from './RegisterProviderContainer';
import { useSignInShellHeader } from 'store/sign-in-shell-header';
import useImplementations from './useImplementations';
import NoticeMessage from '../Messages/Notice';
import { Clients } from '../../../constants/clients';
import { GoBackButton } from 'components/Button/GoBackButton';
import {
  ImplementationKind,
  isAssistedImplementationKind,
} from '@finch-api/common/dist/internal/connect/authorize';
import { AccountStatus } from '@finch-api/common/dist/external/dashboard/connection-status';
import { EMPLOYER_PREFIX } from 'constants/products';
import { useRegisterStep } from 'services/register-step';
import { step as stepValues } from '../steps';
import { Session } from '@finch-api/common/dist/internal/connect/validate';
import { shouldTriggerAuthFallback } from 'lib/auth-fallback/utils';
import { FinchErrorCode } from 'constants/finch-error-code';
import { AUTOMATED_AUTH_INSTRUCTIONS } from 'constants/automated-auth-instructions';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlag } from 'constants/feature-flags';
import {
  generateScopeForProvider,
  getInstructionConfig,
} from 'utils/scoped-instructions';
import {
  useTrackConversionStep,
  useTrackConversionError,
} from '../../../services/track-conversion';
import { usePreviewModeStore } from '../usePreviewMode';
import { useCameFromAuthFallback } from 'store/came-from-auth-fallback';
import { PREVIEW_MODE_ERROR_MESSAGE } from '../../../constants/error-messages';

export type SubmitOptions = {
  subdomain?: string;
  fullDomain?: string;
  domain?: string;
  username?: string;
  password?: string;
  apiToken?: string;
  companyId?: string;
  /**
   * Customer-supplied company legal name. Used by providers whose API exposes
   * no company identity of its own — Warp, whose sign-in page collects it.
   */
  companyName?: string;
  providerClientId?: string;
  providerClientSecret?: string;
  providerRedirectUri?: string;
  isolvedUrl?: string;
  clientCode?: string;
  SID?: string;
  step?: string;
  reportUrl?: string;
  tenantId?: string;
  apiBaseUrl?: string;
  codeVerifier?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  idpRedirect?: boolean;
  firmCode?: string;
};

// if the connection is connected, the products haven't changed, and the assisted status is not connected,
// then we need to show just the manual sign in screen
const hasConnectionScopesChanged = (
  connection: Session['connection'],
  products: string[],
) => {
  return (
    connection &&
    connection.automatedStatus === AccountStatus.CONNECTED &&
    // We will only skip the automated screen if the existing connection
    // scopes match match the requested products
    isEqual(
      [...connection.automatedScopes, ...connection.assistedScopes].map(
        (scope) => scope.replace(EMPLOYER_PREFIX, ''),
      ),
      products,
    ) &&
    connection.assistedStatus &&
    connection.assistedStatus !== AccountStatus.CONNECTED
  );
};

const SignIn = ({
  clientId,
  connectionId,
  connection,
  clientName,
  redirectUri,
  products,
  sessionKey,
  currentBrowserSessionKey,
  state,
  category,
  providersConfig,
  payrollProvider,
  handleAuthorizeResponse,
  sandbox,
  client,
  prevStep,
  setCurrentStep,
  sdkVersion,
  appType,
  hasBenefitsProduct,
  implementationHasAssistedBenefits,
  setProviderImplementation,
  currentStep,
  setProviderToRedirect,
  error,
  setError,
  authorizeLoading,
  setAuthorizeLoading,
  idpRedirect,
  setRequestFinished,
  setDelayedAuthorizeLoading,
}: AuthorizeContextValue) => {
  const authFailureCounts = useAuthFailureCount(
    (state) => state.authFailureCounts,
  );
  const goBackOverride = useSignInShellHeader(
    (state) => state.goBackOverride,
  );
  const cameFromAuthFallback = useCameFromAuthFallback(
    (state) => state.cameFromAuthFallback,
  );
  const registerStep = useRegisterStep();
  const trackConversion = useTrackConversionStep();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      trackConversion({
        action: 'display',
      });
      registerStep({ step: stepValues.SIGN_IN });
      mounted.current = true;
    }
  }, [trackConversion]);

  const trackConversionError = useTrackConversionError();

  if (!payrollProvider) throw new Error('no payroll provider');

  const { previewMode } = usePreviewModeStore();

  const {
    implementationKind,
    onSwitchImplementationKind,
    implementationDetail,
  } = useImplementations({ provider: payrollProvider, setError, idpRedirect });

  useEffect(() => {
    setProviderImplementation(implementationDetail);
  }, [implementationDetail, setProviderImplementation, registerStep]);

  // Universal auto-focus for any provider component loaded from the map
  useEffect(() => {
    const focusFirstInput = () => {
      const firstInput = document.querySelector('input');
      if (firstInput instanceof HTMLInputElement) {
        firstInput.focus();
      }
    };

    focusFirstInput();
  }, [implementationDetail?.id, payrollProvider?.id]); // Re-run when implementation or provider changes

  useEffect(() => {
    if (
      isAssistedImplementationKind(implementationKind) ||
      hasConnectionScopesChanged(connection, products || []) ||
      shouldTriggerAuthFallback(client, authFailureCounts, implementationDetail)
    ) {
      setCurrentStep(stepValues.MANUAL_SIGN_IN);
    }
  }, [
    authFailureCounts,
    client,
    connection,
    implementationDetail,
    implementationKind,
    products,
    setCurrentStep,
  ]);

  // Feature flag must be called before any conditional returns
  const popOutInstructionsEnabled = useFlag(
    FeatureFlag.PopOutInstructionsEnabled,
  );

  if (!client) return <Container>{!client && <LoadingSpinner />}</Container>;
  if (!providersConfig) throw new Error('no provider configs');
  if (!products) throw new Error('no products');

  // it should either return api or credential implementation detail
  if (!implementationDetail)
    throw new Error(
      `${payrollProvider.id} ${implementationKind} kind does not have an implementation detail`,
    );

  const ImplementationComponent = getProviderComponent(
    payrollProvider.id as PayrollProviderId,
    implementationKind,
  );

  if (!ImplementationComponent)
    return (
      <Container>{`Automated flows do not support the ${implementationKind} authentication method.`}</Container>
    );

  const handleSubmit = async (fieldToValueMap: SubmitOptions) => {
    const {
      subdomain,
      fullDomain,
      domain,
      username,
      password,
      apiToken,
      companyId,
      companyName,
      providerClientId,
      providerClientSecret,
      providerRedirectUri,
      clientCode,
      SID,
      step,
      reportUrl,
      tenantId,
      apiBaseUrl,
      codeVerifier,
      codeChallenge,
      codeChallengeMethod,
      isolvedUrl,
      firmCode,
    } = fieldToValueMap;

    trackConversion({
      action: 'submit',
    });
    setRequestFinished(false);
    setAuthorizeLoading(true);
    setError(null);

    if (previewMode) {
      setError({
        message: PREVIEW_MODE_ERROR_MESSAGE,
        omitSessionKey: true,
        status: null,
      });

      setDelayedAuthorizeLoading(false);

      return;
    }

    if (subdomain) {
      if (!validateSubdomain(subdomain)) {
        setError({
          message:
            'Invalid subdomain. Subdomain should contain no spaces, symbols, or http prefixes',
          omitSessionKey: true,
          status: null,
        });
        setDelayedAuthorizeLoading(false);
        return;
      }
      setError(null);
    }

    try {
      // Record that the user submitted the sign in form and reached that step
      registerStep({
        step: currentStep,
        providerId: payrollProvider.id,
      });

      const authorizeResponse = await authorize({
        clientId,
        connectionId,
        clientName,
        redirectUri,
        products,
        username,
        password,
        providerClientId,
        providerClientSecret,
        providerRedirectUri,
        companyId,
        companyName,
        clientCode,
        domain,
        subdomain,
        fullDomain,
        reportUrl,
        tenantId,
        apiBaseUrl,
        apiToken,
        SID,
        sessionKey,
        currentBrowserSessionKey,
        state,
        category,
        payrollProviderId: payrollProvider.id,
        providerImplementationId: implementationDetail.id,
        sandbox,
        sdkVersion,
        step,
        appType,
        codeChallenge,
        codeChallengeMethod,
        codeVerifier,
        implementationHasAssistedBenefits,
        isolvedUrl,
        firmCode,
        acfFallbackEligible:
          cameFromAuthFallback ||
          shouldTriggerAuthFallback(client, authFailureCounts, implementationDetail),
      });

      handleAuthorizeResponse(authorizeResponse);
    } catch (err: any) {
      const finchCode = err?.response?.data?.finch_code;
      const formattedMessage = err?.response?.data?.formatted_message;
      if (finchCode === FinchErrorCode.IncorrectPayrollProvider) {
        const correctPayrollProvider = _.get(
          err,
          'response.data.correct_payroll_provider',
        );
        setProviderToRedirect(correctPayrollProvider);
      }

      trackConversionError({
        error: {
          statusCode: getStatusCode(err),
          finchCode,
        },
      });

      setError({
        message:
          err.message || 'An unexpected error has occurred. Please try again.',
        formattedMessage,
        status: getStatusCode(err),
        finchCode,
      });

      setDelayedAuthorizeLoading(false);
    }
  };

  const handleMockOAuth = async () => {
    const isReauth = !!connectionId;
    const isFinchSandbox = sandbox === 'finch';
    const isDemoApp = clientId === Clients.Finch.Homepage;

    if (!isFinchSandbox && !isDemoApp) {
      return false;
    }

    if (isFinchSandbox) {
      if (isReauth) {
        await handleSubmit({
          step: 'REAUTH_SANDBOX_OAUTH_CONNECTION',
        });
      } else {
        await handleSubmit({
          step: 'NEW_SANDBOX_OAUTH_CONNECTION',
        });
      }

      return true;
    }

    if (isDemoApp) {
      await handleSubmit({});

      return true;
    }

    return false;
  };

  // Determine instructions link based on feature flag
  let instructionsLink: string | undefined;
  let instructionsInnerHtmlToScroll: string | undefined;
  let instructionsInnerHtmlToScrollStep2: string | undefined;
  let instructionsInnerHtmlToScrollStep3: string | undefined;
  let instructionsInnerHtmlToScrollStep4: string | undefined;
  let instructionsInnerHtmlToScrollStep5: string | undefined;
  let instructionsInnerHtmlToScrollStep6: string | undefined;
  let instructionsInnerHtmlToScrollStep7: string | undefined;
  let instructionsInnerHtmlToScrollStep8: string | undefined;

  if (popOutInstructionsEnabled) {
    // Use scoped instructions when feature flag is enabled
    const implKind = implementationDetail.kind.replace(
      'finch_sandbox_',
      '',
    ) as ImplementationKind;

    const scope = generateScopeForProvider(
      payrollProvider.id as PayrollProviderId,
      implKind,
      products,
    );

    const config = getInstructionConfig(
      payrollProvider.id as PayrollProviderId,
      implKind,
      scope,
    );

    if (config) {
      instructionsLink = config.url;
      // Get step-1 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-1']) {
        instructionsInnerHtmlToScroll = config.innerHtmlToScroll['step-1'];
      } else if (config.innerHtmlToScroll && Object.keys(config.innerHtmlToScroll).length > 0) {
        // Fallback to first innerHtmlToScroll if 'step-1' not available
        instructionsInnerHtmlToScroll = Object.values(config.innerHtmlToScroll)[0];
      }
      // Get step-2 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-2']) {
        instructionsInnerHtmlToScrollStep2 = config.innerHtmlToScroll['step-2'];
      }
      // Get step-3 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-3']) {
        instructionsInnerHtmlToScrollStep3 = config.innerHtmlToScroll['step-3'];
      }
      // Get step-4 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-4']) {
        instructionsInnerHtmlToScrollStep4 = config.innerHtmlToScroll['step-4'];
      }
      // Get step-5 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-5']) {
        instructionsInnerHtmlToScrollStep5 = config.innerHtmlToScroll['step-5'];
      }
      // Get step-6 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-6']) {
        instructionsInnerHtmlToScrollStep6 = config.innerHtmlToScroll['step-6'];
      }
      // Get step-7 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-7']) {
        instructionsInnerHtmlToScrollStep7 = config.innerHtmlToScroll['step-7'];
      }
      // Get step-8 innerHtmlToScroll
      if (config.innerHtmlToScroll && config.innerHtmlToScroll['step-8']) {
        instructionsInnerHtmlToScrollStep8 = config.innerHtmlToScroll['step-8'];
      }
    }
  }

  // Fallback to existing AUTOMATED_AUTH_INSTRUCTIONS if scoped instructions not available
  if (!instructionsLink) {
    instructionsLink =
      AUTOMATED_AUTH_INSTRUCTIONS[payrollProvider.id as PayrollProviderId]?.[
        implementationDetail.kind.replace(
          'finch_sandbox_',
          '',
        ) as ImplementationKind
      ];
  }

  return (
    <>
      {providersConfig.length > 1 && (
        <GoBackButton
          onClick={() => (goBackOverride ? goBackOverride() : prevStep())}
        />
      )}
      <RegisterProviderContainer
        client={client}
        error={error}
        setError={setError}
        provider={payrollProvider}
        products={products}
        hasBenefitsProduct={hasBenefitsProduct}
        currentStep={currentStep}
        implementationDetail={implementationDetail}
        sandboxMode={sandbox}
        connection={connection}
      >
        {sandbox === 'provider' &&
          hasBenefitsProduct &&
          implementationHasAssistedBenefits && (
            <NoticeMessage>
              The benefits product is being requested, but assisted benefits is
              not supported in Sandbox mode. Any generated tokens for this
              provider will not have benefits scopes.
            </NoticeMessage>
          )}
        <ImplementationComponent
          client={client}
          provider={payrollProvider}
          implementationDetail={implementationDetail}
          isLoading={authorizeLoading}
          onSubmit={handleSubmit}
          handleClick={onSwitchImplementationKind}
          products={products}
          sandboxMode={sandbox}
          instructionsLink={instructionsLink}
          instructionsInnerHtmlToScroll={instructionsInnerHtmlToScroll}
          instructionsInnerHtmlToScrollStep2={instructionsInnerHtmlToScrollStep2}
          instructionsInnerHtmlToScrollStep3={instructionsInnerHtmlToScrollStep3}
          instructionsInnerHtmlToScrollStep4={instructionsInnerHtmlToScrollStep4}
          instructionsInnerHtmlToScrollStep5={instructionsInnerHtmlToScrollStep5}
          instructionsInnerHtmlToScrollStep6={instructionsInnerHtmlToScrollStep6}
          instructionsInnerHtmlToScrollStep7={instructionsInnerHtmlToScrollStep7}
          instructionsInnerHtmlToScrollStep8={instructionsInnerHtmlToScrollStep8}
          onMockOAuth={handleMockOAuth}
        />
      </RegisterProviderContainer>
    </>
  );
};

export default withAuthorizeContext(SignIn);
